import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnswerPhoto } from '../../models';
import { ChatStrings } from '../../i18n';
import { ChevronIcon, PhotoIcon } from '../Icons';
import PhotoViewer from './PhotoViewer';
import { galleryCards } from './gallery';

interface AnswerPhotosProps {
   photos: AnswerPhoto[];
   strings: ChatStrings;
}

export const prefersReducedMotion = () =>
   typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** How far a card starts from the row's start edge (the right edge in right-to-left). */
const offsetFromStart = (strip: HTMLElement, slide: HTMLElement) => {
   const edge = strip.getBoundingClientRect();
   const box = slide.getBoundingClientRect();
   return getComputedStyle(strip).direction === 'rtl' ? edge.right - box.right : box.left - edge.left;
};

/**
 * The row's scroll position where a card snaps to its start: how far the card is from the first one, which sits there
 * at the start (negative right-to-left). An absolute position, since a scroll by a few pixels is snapped on to the next
 * card.
 */
const snapPositionOf = (strip: HTMLElement, first: HTMLElement, slide: HTMLElement) => {
   const start = first.getBoundingClientRect();
   const box = slide.getBoundingClientRect();
   return getComputedStyle(strip).direction === 'rtl' ? box.right - start.right : box.left - start.left;
};

/**
 * The hotel's photos of the places an answer cites, as one small gallery at the end of the answer, never between its
 * lines: a card per photo (one of each place first, in the order the answer names them; see `gallery.ts`), its place's
 * name in bold under it and the hotel's caption. The row snaps card by card with the next one showing at the edge, the
 * position follows the card in view, and a tap opens the photos larger. A card holds its shape before its photo loads,
 * and keeps its place with a plain stand-in if the photo fails. Nothing moves by itself.
 */
const AnswerPhotos: React.FC<AnswerPhotosProps> = ({ photos, strings }) => {
   const cards = useMemo(() => galleryCards(photos), [photos]);
   const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
   // The larger view steps through the photos that loaded, in the gallery's order
   const viewable = useMemo(() => cards.filter((card) => !failed.has(card.photo.url)), [cards, failed]);
   const [current, setCurrent] = useState(0);
   const [viewing, setViewing] = useState<number | null>(null);
   const strip = useRef<HTMLDivElement>(null);
   const slides = useRef<(HTMLElement | null)[]>([]);
   const openers = useRef<(HTMLButtonElement | null)[]>([]);
   const returnFocusTo = useRef<number | null>(null);
   const frame = useRef(0);

   // The card nearest the row's start edge is the current one
   const onScroll = useCallback(() => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
         const edge = strip.current;
         if (!edge) return;
         let nearest = 0;
         let distance = Infinity;
         slides.current.forEach((slide, index) => {
            if (!slide) return;
            const away = Math.abs(offsetFromStart(edge, slide));
            if (away < distance) {
               distance = away;
               nearest = index;
            }
         });
         setCurrent(nearest);
      });
   }, []);
   useEffect(() => () => cancelAnimationFrame(frame.current), []);

   const goTo = (index: number, instantly = false) => {
      const target = Math.max(0, Math.min(index, cards.length - 1));
      setCurrent(target);
      const edge = strip.current;
      const first = slides.current[0];
      const slide = slides.current[target];
      if (!edge || !first || !slide || typeof edge.scrollTo !== 'function') return;
      edge.scrollTo({ left: snapPositionOf(edge, first, slide), behavior: instantly || prefersReducedMotion() ? 'auto' : 'smooth' });
   };

   // Closing the larger view leaves the gallery on the photo seen last: put there at once while the viewer still covers
   // it, so nothing moves in sight, and the focus back on its card without the browser scrolling the row again
   const closeViewer = (last: number) => {
      const index = Math.max(0, cards.indexOf(viewable[last]));
      goTo(index, true);
      returnFocusTo.current = index;
      setViewing(null);
   };
   useEffect(() => {
      if (viewing !== null || returnFocusTo.current === null) return;
      openers.current[returnFocusTo.current]?.focus({ preventScroll: true });
      returnFocusTo.current = null;
   }, [viewing]);

   if (cards.length === 0) return null;
   const many = cards.length > 1;
   const position = Math.min(current, cards.length - 1);

   return (
      <section className="cx-photos" aria-roledescription="carousel" aria-label={strings.photos}>
         <div className="cx-photos__strip" ref={strip} onScroll={many ? onScroll : undefined}>
            {cards.map((card, index) => {
               const { photo } = card;
               const shape = { aspectRatio: String(card.ratio) };
               return (
                  <figure
                     key={`${photo.url}-${index}`}
                     ref={(element) => {
                        slides.current[index] = element;
                     }}
                     className="cx-photo"
                     role="group"
                     aria-roledescription="slide"
                     aria-label={strings.photoSlide(index + 1, cards.length, card.title)}
                  >
                     {failed.has(photo.url) ? (
                        <span className="cx-photo__frame cx-photo__frame--missing" style={shape} role="img" aria-label={strings.photoMissing}>
                           <PhotoIcon />
                        </span>
                     ) : (
                        <button
                           type="button"
                           className="cx-photo__frame cx-photo__open"
                           style={shape}
                           ref={(element) => {
                              openers.current[index] = element;
                           }}
                           onClick={() => setViewing(viewable.indexOf(card))}
                        >
                           <img
                              src={photo.url}
                              alt={card.alt}
                              width={photo.width}
                              height={photo.height}
                              loading={index === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                              onError={() => setFailed((previous) => new Set(previous).add(photo.url))}
                           />
                           <span className="cx-sr-only">{strings.enlargePhoto}</span>
                        </button>
                     )}
                     <figcaption className="cx-photo__caption">
                        <span className="cx-photo__title" dir="auto">
                           {card.title}
                        </span>
                        {card.caption && (
                           <span className="cx-photo__note" dir="auto">
                              {card.caption}
                           </span>
                        )}
                     </figcaption>
                  </figure>
               );
            })}
         </div>

         {many && (
            <div className="cx-photos__controls">
               <button type="button" className="cx-icon-btn" onClick={() => goTo(position - 1)} disabled={position === 0} aria-label={strings.previousPhoto}>
                  <ChevronIcon className="cx-chevron cx-chevron--back" />
               </button>
               <span className="cx-photos__position" aria-hidden="true">
                  {position + 1} / {cards.length}
               </span>
               <button
                  type="button"
                  className="cx-icon-btn"
                  onClick={() => goTo(position + 1)}
                  disabled={position === cards.length - 1}
                  aria-label={strings.nextPhoto}
               >
                  <ChevronIcon className="cx-chevron" />
               </button>
            </div>
         )}

         {viewing !== null && viewable.length > 0 && (
            <PhotoViewer photos={viewable.map((card) => card.photo)} start={viewing} strings={strings} onClose={closeViewer} />
         )}
      </section>
   );
};

export default AnswerPhotos;
